/**
 * Generated-artifact lifecycle (#340 item 3).
 *
 * Quote PDFs, invoices, and contractor job sheets are rebuilt server-side
 * after a generation trigger; the SPA can't observe completion directly, so
 * it polls its own workspace snapshot until the artifact URLs differ from
 * the pre-trigger ones. This module owns that observation end to end — which
 * fields fingerprint each artifact, the poll timeouts, and the sent-state
 * mask that a completed regeneration clears. The poll loop doubles as the
 * post-mutation refetch, so callers run it under `refetch: false`.
 *
 * Which field fingerprints the quote depends on the booking: packages bookings
 * carry per-package draft URLs, the single-quote flow carries one booking-level
 * URL. See {@link quoteDraftUrls} — getting this wrong is a silent 90s hang,
 * not an error.
 *
 * Timeouts are ceilings, not failures: a poll that times out resolves with
 * whatever the server last returned (pollUntilChange semantics), so a slow
 * generation degrades to "data catches up on a later refetch" rather than a
 * spurious error.
 */
import { useState } from "react";
import { pollUntilChange } from "./poll-until";
import { isActive } from "./assignment-status";
import type { WorkspaceEventData } from "./event-workspace-api";

type Snapshot = WorkspaceEventData | null;
type FetchSnapshot = () => Promise<Snapshot>;

export interface GeneratedArtifactUrls {
  quotePdf: string | null;
  depositInvoice: string | null;
  balanceInvoice: string | null;
}

/** Full regeneration (gofpdf render + file-store upload per draft) comfortably
 *  lands within this. */
const GENERATION_TIMEOUT_MS = 90_000;
/** Contractor notification fans out per-assignment on the server; the first
 *  status flip (or the booking stamp) normally lands within a few polls. */
const NOTIFY_FANOUT_TIMEOUT_MS = 60_000;
const NOTIFY_FANOUT_INTERVAL_MS = 1500;
/**
 * Re-issued invoices ride the same generation run and land shortly after the
 * quote PDF; this wait is best-effort so it gets a short leash.
 */
const INVOICE_REISSUE_TIMEOUT_MS = 20_000;

export function generatedArtifactUrls(
  snapshot: WorkspaceEventData | null,
): GeneratedArtifactUrls {
  return {
    quotePdf: snapshot?.documents.quotePdf ?? null,
    depositInvoice: snapshot?.documents.depositInvoice ?? null,
    balanceInvoice: snapshot?.documents.balanceInvoice ?? null,
  };
}

export function artifactUrlChanged(
  previous: string | null | undefined,
  current: string | null | undefined,
): boolean {
  return !!current && current !== (previous ?? null);
}

/**
 * The quote draft URLs a generation run actually rewrites, keyed by package.
 *
 * A packages booking (#20) has no booking-level quote draft: generation writes
 * one draft per package (`quote_packages.draft_file_id`), which surfaces
 * as `quotePackages[].draft_pdf_url`. It never touches
 * `bookings.quote_draft_file_id`, so `documents.quotePdf` — a draft-first
 * coalesce of draft → sent — stays frozen at the sent URL (or null) forever.
 * Gating the poll on `documents.quotePdf` therefore never fires on a packages
 * booking and burns the full timeout on work that already finished.
 *
 * The single-quote flow keeps its old behaviour exactly: one entry, keyed "",
 * carrying `documents.quotePdf`.
 */
export function quoteDraftUrls(snapshot: Snapshot): Map<string, string | null> {
  const packages = snapshot?.quotePackages ?? [];
  if (packages.length === 0) {
    return new Map([["", snapshot?.documents.quotePdf ?? null]]);
  }
  return new Map(packages.map((p) => [p.key, p.draft_pdf_url ?? null]));
}

/**
 * A quote generation has landed once every current draft URL is present and
 * differs from its pre-trigger one. "Every" is safe here because the generate
 * POST is synchronous — it returns only after every draft is persisted — so
 * the first poll fetch normally already satisfies this.
 */
export function quoteArtifactChanged(
  previous: Map<string, string | null>,
  current: Map<string, string | null>,
): boolean {
  if (current.size === 0) return false;
  return [...current].every(([key, url]) =>
    artifactUrlChanged(previous.get(key) ?? null, url),
  );
}

export function shouldWaitForInvoiceReissue(
  previous: GeneratedArtifactUrls,
): boolean {
  return !!previous.depositInvoice || !!previous.balanceInvoice;
}

export function invoiceArtifactChanged(
  previous: GeneratedArtifactUrls,
  current: GeneratedArtifactUrls,
): boolean {
  return (
    artifactUrlChanged(previous.depositInvoice, current.depositInvoice) ||
    artifactUrlChanged(previous.balanceInvoice, current.balanceInvoice)
  );
}

/**
 * Wait for a quote-summary generation to land: a new quote draft URL for every
 * package (or the single booking-level draft), then — only when invoices
 * already existed before the run — their re-issued replacements (best-effort,
 * either invoice counts).
 */
export async function awaitQuoteGeneration(
  before: Snapshot,
  fetchSnapshot: FetchSnapshot,
): Promise<void> {
  const previous = generatedArtifactUrls(before);
  const previousQuoteDrafts = quoteDraftUrls(before);

  const latest = await pollUntilChange<Snapshot>({
    fetchSnapshot,
    hasChanged: (snap) =>
      quoteArtifactChanged(previousQuoteDrafts, quoteDraftUrls(snap)),
    timeoutMs: GENERATION_TIMEOUT_MS,
  });

  if (!shouldWaitForInvoiceReissue(previous)) return;
  await pollUntilChange<Snapshot>({
    initialSnapshot: latest,
    fetchSnapshot,
    hasChanged: (snap) => {
      const current = generatedArtifactUrls(snap);
      return invoiceArtifactChanged(previous, current);
    },
    timeoutMs: INVOICE_REISSUE_TIMEOUT_MS,
  });
}

/**
 * Wait for a job-summary generation to land: every active assignment carries
 * a jobsheet URL that differs from its pre-trigger one. No active crew means
 * there is nothing to wait for.
 */
export async function awaitJobSummaryGeneration(
  before: Snapshot,
  fetchSnapshot: FetchSnapshot,
): Promise<void> {
  const previousJobsheetUrls = new Map(
    (before?.assignments || [])
      .filter(isActive)
      .map((a) => [a.id, a.jobsheetUrl ?? null]),
  );
  await pollUntilChange<Snapshot>({
    fetchSnapshot,
    hasChanged: (snap) => {
      const active = (snap?.assignments || []).filter(isActive);
      if (active.length === 0) return true;
      return active.every((a) => {
        const prev = previousJobsheetUrls.get(a.id) ?? null;
        return !!a.jobsheetUrl && a.jobsheetUrl !== prev;
      });
    },
    timeoutMs: GENERATION_TIMEOUT_MS,
  });
}

/** The slice of the details payload the notification fan-out poll reads. */
interface NotifyFanoutSnapshot {
  assignments?: Array<{ id: string; status: string }>;
  booking?: { contractors_notified_at?: string | null };
}

/**
 * Wait for a contractor notification fan-out to land: an assignment that
 * wasn't already notified/accepted flips to one of those statuses, or the
 * booking is stamped `contractors_notified_at`. The notify POST returns
 * before the fan-out finishes, so the caller's refetch would otherwise race
 * it; run this under `refetch: false` like the generation waits above.
 */
export async function awaitContractorNotification(
  beforeAssignments: Array<{ id: string; status: string }>,
  fetchSnapshot: () => Promise<NotifyFanoutSnapshot | null>,
): Promise<void> {
  const previouslyNotifiedIds = new Set(
    beforeAssignments
      .filter((a) => a.status === "notified" || a.status === "accepted")
      .map((a) => a.id),
  );
  await pollUntilChange<NotifyFanoutSnapshot | null>({
    fetchSnapshot,
    hasChanged: (data) => {
      if (!data) return false;
      const flippedNew = (data.assignments || []).some(
        (a) =>
          !previouslyNotifiedIds.has(a.id) &&
          (a.status === "notified" || a.status === "accepted"),
      );
      const stamped = !!data.booking?.contractors_notified_at;
      return flippedNew || stamped;
    },
    intervalMs: NOTIFY_FANOUT_INTERVAL_MS,
    timeoutMs: NOTIFY_FANOUT_TIMEOUT_MS,
  });
}

/**
 * The PDF link a package tab should open: the freshest draft first, falling
 * back to the last sent copy — the same draft → sent precedence
 * {@link quoteDraftUrls} documents for `documents.quotePdf`.
 */
export function packageQuotePdfUrl(pkg: {
  draft_pdf_url?: string | null;
  sent_pdf_url?: string | null;
}): string | null {
  return pkg.draft_pdf_url || pkg.sent_pdf_url || null;
}

/**
 * The header-summary precedence shared by the artifact sections (quote
 * summary, job summary): out-of-date beats the sent mask beats the generated
 * content, and an ungenerated section always reads "Not generated".
 */
export function artifactSummaryLine(opts: {
  isStale: boolean;
  staleLine: string;
  showMask: boolean;
  maskLine: string;
  /** null = nothing generated yet. */
  contentLine: string | null;
}): string {
  if (opts.isStale) return opts.staleLine;
  if (opts.showMask) return opts.maskLine;
  return opts.contentLine ?? "Not generated";
}

/**
 * The section-side half of the lifecycle: once an artifact has been sent
 * (quote to client, job sheets to contractors), the section masks itself as
 * "sent — regenerate to update" until generatedAt advances past the send.
 * markSent() applies the mask optimistically before the refetch confirms it.
 */
export interface OptimisticMaskInput {
  sentAt: string | null | undefined;
  generatedAt: string | null | undefined;
}

export interface OptimisticMaskResult {
  showMask: boolean;
  markSent: () => void;
}

function isAfter(
  candidate: string | null | undefined,
  baseline: string | null | undefined,
): boolean {
  if (!candidate) return false;
  if (!baseline) return true;
  return new Date(candidate).getTime() > new Date(baseline).getTime();
}

export function useOptimisticMask({
  sentAt,
  generatedAt,
}: OptimisticMaskInput): OptimisticMaskResult {
  const [optimisticAt, setOptimisticAt] = useState<string | null>(null);

  const showMask =
    isAfter(optimisticAt, generatedAt) || isAfter(sentAt, generatedAt);

  return {
    showMask,
    markSent: () => setOptimisticAt(new Date().toISOString()),
  };
}
